import React, { useCallback, useContext, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { ButtonGroup } from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { Repository } from 'src/components/pipelines/models';
import { requestBuildConfiguration } from 'src/components/pipelines/redux/actions/pipelines';
import { getYmlPath } from 'src/components/pipelines/redux/selectors/pipelines';
import { useStatsigGate } from 'src/contexts/feature-provider';

import OnboardingContext from './OnboardingContext';
import { ExistingConfigWrapper } from './styled';

type Props = {
  repository: Repository;
  enablePipelines: () => void;
};

const ExistingConfigSection = ({ repository, enablePipelines }: Props) => {
  const dispatch = useDispatch();

  const { analyticsSource } = useContext(OnboardingContext);
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const ymlPath = useSelector(getYmlPath);

  const isBitbucketPipelinesYmlSupportEnabled = useStatsigGate(
    StatsigFeatureKeys.isBitbucketPipelinesYmlSupportEnabled
  ) as boolean;

  useEffect(() => {
    dispatch(
      requestBuildConfiguration('', isBitbucketPipelinesYmlSupportEnabled)
    );
  }, [dispatch, isBitbucketPipelinesYmlSupportEnabled]);
  const sendAnalyticEvent = useCallback(
    (subjectId: string) => {
      createAnalyticsEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: subjectId,
        source: analyticsSource,
      }).fire();
    },
    [analyticsSource, createAnalyticsEvent]
  );

  const onEnablePipeline = () => {
    sendAnalyticEvent('enablePipeline');
    enablePipelines();
  };

  return (
    <ExistingConfigWrapper>
      <p>
        Looks like you already have a bitbucket-pipelines.yml file in this
        repository
      </p>
      <ButtonGroup>
        <Tooltip
          content={
            !repository.userIsAdmin
              ? 'You need to be an admin to configure Pipelines'
              : null
          }
          position="left"
        >
          <Button
            target="_blank"
            href={ymlPath}
            onClick={() => sendAnalyticEvent('reviewPipeline')}
          >
            Review pipeline
          </Button>
        </Tooltip>
        <Button
          appearance="primary"
          isDisabled={!repository.userIsAdmin}
          onClick={onEnablePipeline}
        >
          Run initial pipeline
        </Button>
      </ButtonGroup>
    </ExistingConfigWrapper>
  );
};

export default React.memo(ExistingConfigSection);
